import type { AuthState } from '../../types';

import { gql } from '@apollo/client';
import { useSetState } from 'minimal-shared/hooks';
import { useApolloClient } from '@apollo/client/react';
import { useMemo, useEffect, useCallback } from 'react';

import { dispatch } from 'src/state';
import * as ActionTypes from 'src/state/action-types';
import { changeLanguage } from 'src/state/ui/actions';
import { workspacesLoaded } from 'src/state/workspace/actions';
import { addMessages } from 'src/state/messages/messages-slice';
import { clearRooms } from 'src/state/websocket/websocket-slice';
import { type ISubscription, SubscriptionFragment } from 'src/state/subscription/types';
import { joinRoom, connectSocket, disconnectSocket, waitForConnection } from 'src/lib/socket';

import { JWT_STORAGE_KEY } from './constant';
import { AuthContext } from '../auth-context';
import { refreshAccessToken } from './action';
import { setSession, isValidToken } from './utils';

// ----------------------------------------------------------------------

const ME_QUERY = gql`
  query Me {
    me {
      id
      username
      email
      firstName
      lastName
      language
      roles
      imageUrl
      subscription {
        ...SubscriptionFragment
      }
      workspaces {
        edges {
          role
          node {
            id
            name
          }
        }
      }
      messages {
        id
        title
        type
        category
        isRead
        createdAt
        avatarUrl
        toastSettings {
          showToast
          duration
          variant
          action {
            label
            onClick
          }
          cancelLabel
        }
      }
      featurebase_token
    }
  }
  ${SubscriptionFragment}
`;

type MeQueryData = {
  me: {
    id: string;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    language?: string;
    roles: string[];
    imageUrl?: string;
    subscription: ISubscription;
    workspaces: {
      edges: Array<{
        role: string;
        node: {
          id: string;
          name: string;
        };
      }>;
    };
    messages: Array<{
      id: string;
      title: string;
      type: string;
      category: string;
      isRead: boolean;
      createdAt: number;
      avatarUrl: string | null;
      toastSettings?: {
        showToast?: boolean;
        duration?: number;
        variant?: string;
        action?: {
          label: string;
          onClick: string;
        };
        cancelLabel?: string;
      };
    }>;
    featurebase_token?: string;
  };
};

/**
 * NOTE:
 * We only build demo at basic level.
 * Customer will need to do some extra handling yourself if you want to extend the logic and other features...
 */

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const apolloClient = useApolloClient();
  const { state, setState } = useSetState<AuthState>({
    user: null,
    loading: true,
  });

  const checkUserSession = useCallback(async () => {
    try {
      const storedToken = sessionStorage.getItem(JWT_STORAGE_KEY);
      let resolvedToken: string | null = null;

      if (storedToken) {
        if (isValidToken(storedToken)) {
          await setSession(storedToken, async () => {
            await refreshAccessToken();
          });
          resolvedToken = storedToken;
        } else {
          await setSession(null);
        }
      }

      const { data } = await apolloClient.query<MeQueryData>({
        query: ME_QUERY,
        fetchPolicy: 'network-only',
      });

      if (data?.me) {
        const user = { ...data.me, accessToken: resolvedToken };
        setState({
          user,
          loading: false,
        });

        // Sync user data to Redux state (single source of truth)
        dispatch({
          type: ActionTypes.AUTH_GETSESSION_SUCCESS,
          payload: {
            user: data.me,
          },
        });

        // Sync user language to i18n and cookie
        if (data.me.language) {
          dispatch(changeLanguage(data.me.language));
        }

        // Load workspaces into Redux state
        if (data.me.workspaces?.edges) {
          const workspaces = data.me.workspaces.edges.map((edge) => ({
            id: edge.node.id,
            name: edge.node.name,
            role: edge.role,
            logo: `/assets/icons/workspaces/logo-1.webp`,
          }));
          dispatch(workspacesLoaded(workspaces));
        }

        // Load messages into Redux state
        if (data.me.messages && data.me.messages.length > 0) {
          // Convert duration=0 back to Infinity (GraphQL Float can't represent Infinity)
          const messagesWithInfinity = data.me.messages.map((msg) => {
            const message: any = {
              ...msg,
              toastSettings: msg.toastSettings
                ? {
                    showToast: msg.toastSettings.showToast,
                    duration:
                      msg.toastSettings.duration === 0 ? Infinity : msg.toastSettings.duration,
                    variant: msg.toastSettings.variant as
                      | 'info'
                      | 'success'
                      | 'warning'
                      | 'error'
                      | undefined,
                    action: msg.toastSettings.action,
                    cancelLabel: msg.toastSettings.cancelLabel,
                  }
                : undefined,
            };
            return message;
          });
          dispatch(addMessages(messagesWithInfinity));
        }

        // Connect the socket now that the auth cookie is set, so the
        // WebSocket handshake is authenticated. connectSocket() is a no-op
        // if already connected.
        connectSocket();

        // Join user's WebSocket room to receive real-time messages
        waitForConnection().then(() => {
          joinRoom('user', data.me.id, 'user');
        });
      } else {
        await setSession(null);
        setState({ user: null, loading: false });

        // Clear Redux state on logout
        dispatch({
          type: ActionTypes.AUTH_LOGOUT_REQUEST,
        });
        // Disconnect socket and clear rooms so the next login gets a fresh
        // authenticated connection.
        disconnectSocket();
        dispatch(clearRooms());
      }
    } catch (error) {
      console.error(error);
      await setSession(null);
      setState({ user: null, loading: false });

      // Clear Redux state on error
      dispatch({
        type: ActionTypes.AUTH_GETSESSION_FAILURE,
        errorCode: error instanceof Error ? error.message : 'Authentication failed',
      });
      // Disconnect socket and clear rooms on auth error
      disconnectSocket();
      dispatch(clearRooms());
    }
  }, [apolloClient, setState]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user ? { ...state.user, role: state.user?.role ?? 'admin' } : null,
      checkUserSession,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
    }),
    [checkUserSession, state.user, status]
  );

  return <AuthContext value={memoizedValue}>{children}</AuthContext>;
}
